import React, { useState, useEffect, useContext, useCallback } from 'react'
import { AuthContext } from '@/layout/Auth'
import { useNavigate } from 'react-router-dom'
import { styled } from '@mui/material/styles'
import { secondaryText } from '@/constant'
import { Stack } from '@mui/material'
import { Row } from '../components'
import Button, { ConfirmButton } from '@/components/Button'
import { MemberInfo, Organizationinfo } from '@/types'
import Text from '@/components/Text'
import TransferList from '@/components/TransferList'
import { useQuery, useMutation } from '@tanstack/react-query'
import { getRoles, updateMemberRoles } from '@/api'
import { confirm } from '@/components/Modal'
import { Message } from '@cx/ui'
import { ensureSecurityGate } from '@/utils/securityGate'

export interface RoleEditorProps {
  userInfo: MemberInfo
  orgInfo: Organizationinfo
  refetch: () => void
  onCancel: () => void
}

const HightlightText = styled(Text)(() => ({
  fontSize: '14px',
  weight: 'bold',
  margin: '0px 4px',
}))

const RoleEditor: React.FC<RoleEditorProps> = (props) => {
  const navigate = useNavigate()
  const { userInfo, onCancel, refetch } = props
  const { authInfo } = useContext(AuthContext)
  const orgInfo = authInfo?.orgInfo
  const [roleIds, setRoleIds] = useState<number[]>(userInfo?.roles?.map((role) => role.id))
  const { data: res } = useQuery(
    ['getRoles', orgInfo?.id],
    // @ts-ignore
    () => orgInfo?.id && getRoles(orgInfo?.id),
    {
      enabled: !!orgInfo?.id,
    },
  )
  const updateUserRoleMutation = useMutation(updateMemberRoles, {
    onError: (err: string | number) => {
      if (err == 4123) {
        confirm({
          title: '提示',
          content: '空间至少有一名管理员',
          hideClose: true,
          closeAfterClose: true,
          // @ts-ignore
          onConfirm: null,
          // @ts-ignore
          onClose: null,
        })
      }
    },
    onSuccess: () => {
      Message.success('更新角色成功')
      refetch()
      onCancel()
    },
  })

  const handleChangeRoles = useCallback((ids: number[]) => {
    setRoleIds(ids)
  }, [])

  const handleUpdateUserRole = useCallback(() => {
    ;(async () => {
      if (!(await ensureSecurityGate('admin_operation'))) return
      updateUserRoleMutation.mutate({
        // @ts-ignore
        id: orgInfo?.id,
        user_id: userInfo?.id as number,
        query: { role_ids: roleIds },
      })
    })()
  }, [orgInfo?.id, roleIds, updateUserRoleMutation, userInfo?.id])

  useEffect(() => {
    if (userInfo && orgInfo) {
      setRoleIds(userInfo?.roles?.map((role) => role.id))
    } else {
      navigate('/console/organization/member')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo])

  return (
    <Stack sx={{ height: '100%', gap: '24px' }}>
      <Stack sx={{ gap: '8px' }}>
        <Text weight='bold'>修改用户角色</Text>
        <Text
          sx={{
            display: 'flex',
            fontSize: '14px',
            alignItems: 'center',
            color: secondaryText,
          }}
          weight='regular'
        >
          确认将用户
          <HightlightText weight='bold'>{userInfo?.nickname}</HightlightText>
          在空间
          <HightlightText>{orgInfo?.name}</HightlightText>
        </Text>
      </Stack>
      <TransferList
        id='id'
        data={res?.data ?? []}
        searchKey='name'
        checked={roleIds ?? []}
        // @ts-ignore
        onChange={handleChangeRoles}
      />
      <Row sx={{ justifyContent: 'flex-end', gap: '8px' }}>
        <Button type='secondary' onClick={onCancel}>
          取消
        </Button>
        <ConfirmButton onClick={handleUpdateUserRole}>确认修改</ConfirmButton>
      </Row>
    </Stack>
  )
}

export default RoleEditor
